import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, User, Menu, X, ChevronDown } from "lucide-react";
import { useStorefront, navCategories } from "../lib/storefront";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(null);
  const { count } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  // Menu built from the CRM categories + catalog (falls back to the old list).
  const NAV = navCategories(useStorefront());

  const handleSearch = (e) => {
    e.preventDefault();
    const q = e.target.q.value.trim();
    if (q) navigate(`/shop?q=${encodeURIComponent(q)}`);
    setSearchOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-neutral-200">
      {/* Top promo bar */}
      <div className="bg-[#1a1a1a] text-white text-center py-2 text-xs tracking-wide">
        <span className="hidden sm:inline">New here? </span>Get <strong>20% off</strong> your first order with code <strong className="text-[#2C66E3]">LETSPRINT20</strong>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between gap-3 lg:gap-5 xl:gap-8 h-[68px]">
          {/* Mobile menu */}
          <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 -ml-2" aria-label="Menu">
            <Menu className="w-6 h-6" />
          </button>

          {/* Logo */}
          <Link to="/" className="flex items-center select-none shrink-0 lg:mr-4 xl:mr-6" aria-label="Let’s Print home">
            <img
              src="https://customer-assets.emergentagent.com/job_print-migration-1/artifacts/cxdbl1o1_LP%20FINAL%20LOGO%20WEBSITE.png"
              alt="Let’s Print"
              className="h-9 lg:h-10 w-auto max-w-[180px] object-contain"
              draggable="false"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-3 xl:gap-5 text-[13px] xl:text-[14px] font-medium">
            {NAV.map((cat, idx) => (
              <div
                key={cat.slug}
                className="relative"
                onMouseEnter={() => setHoverIdx(idx)}
                onMouseLeave={() => setHoverIdx(null)}
              >
                <Link
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-1 py-6 whitespace-nowrap hover:text-[#2C66E3] transition-colors"
                >
                  {cat.label}
                  <ChevronDown className="w-3.5 h-3.5" />
                </Link>
                {hoverIdx === idx && (cat.sections || (cat.items && cat.items.length > 0)) && (
                  cat.sections ? (
                    // Mega menu — multi-column when the category provides `sections`.
                    <div
                      className="absolute top-full left-1/2 -translate-x-1/2 bg-white border border-neutral-200 shadow-xl rounded-xl py-5 px-6 z-50 flex gap-8"
                      style={{ minWidth: `${Math.min(cat.sections.length * 220, 760)}px` }}
                      data-testid={`mega-menu-${cat.slug}`}
                    >
                      {cat.sections.map((section) => (
                        <div key={section.title} className="min-w-[200px]">
                          <p className="text-[10px] tracking-widest text-[#2C66E3] uppercase font-extrabold mb-2.5">
                            {section.title}
                          </p>
                          <div className="space-y-0.5">
                            {section.items.map((item) => {
                              const isObj = typeof item === "object" && item !== null;
                              const label = isObj ? item.label : item;
                              const to = isObj
                                ? (item.to ?? (item.slug ? `/product/${item.slug}` : `/category/${cat.slug}`))
                                : `/category/${cat.slug}`;
                              return (
                                <Link
                                  key={label}
                                  to={to}
                                  data-testid={`menu-item-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                  className="block px-2 py-1.5 -mx-2 text-sm text-neutral-700 hover:bg-[#EEF3FE] hover:text-[#2C66E3] rounded transition-colors"
                                >
                                  {label}
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 bg-white border border-neutral-200 shadow-xl rounded-xl py-3 min-w-[220px] z-50">
                      {cat.items.map((item) => {
                        const isObj = typeof item === "object" && item !== null;
                        const label = isObj ? item.label : item;
                        const to = isObj
                          ? (item.to ?? (item.slug ? `/product/${item.slug}` : `/category/${cat.slug}`))
                          : `/category/${cat.slug}`;
                        return (
                          <Link
                            key={label}
                            to={to}
                            data-testid={`menu-item-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className="block px-5 py-2.5 text-sm text-neutral-700 hover:bg-[#EEF3FE] hover:text-[#2C66E3] transition-colors"
                          >
                            {label}
                          </Link>
                        );
                      })}
                    </div>
                  )
                )}
              </div>
            ))}
          </nav>

          {/* Right icons */}
          <div className="flex items-center gap-1">
            <button onClick={() => setSearchOpen((v) => !v)} className="p-2.5 hover:bg-neutral-100 rounded-full transition-colors" aria-label="Search" title="Search products">
              <Search className="w-5 h-5" />
            </button>
            <Link
              to={user ? "/account" : "/sign-in"}
              data-testid="header-account-link"
              className="p-2.5 hover:bg-neutral-100 rounded-full transition-colors hidden sm:flex items-center"
              aria-label={user ? "My Account" : "Sign in"}
              title={user ? "My Account" : "Sign in / Create account"}
            >
              {user ? (
                user.picture ? (
                  <img src={user.picture} alt="" className="w-6 h-6 rounded-full object-cover" />
                ) : (
                  <span className="w-6 h-6 rounded-full bg-[#2C66E3] text-white text-[11px] font-black flex items-center justify-center">
                    {(user.name || user.email).trim().charAt(0).toUpperCase()}
                  </span>
                )
              ) : (
                <User className="w-5 h-5" />
              )}
            </Link>
            <Link to="/cart" className="p-2.5 hover:bg-neutral-100 rounded-full transition-colors relative" aria-label="Cart" title="View cart">
              <ShoppingBag className="w-5 h-5" />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#2C66E3] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {count}
                </span>
              )}
            </Link>
          </div>
        </div>

        {searchOpen && (
          <form onSubmit={handleSearch} className="py-3 border-t border-neutral-200">
            <div className="flex items-center gap-3">
              <Search className="w-5 h-5 text-neutral-500" />
              <input
                name="q"
                autoFocus
                placeholder="Search business cards, banners, stickers…"
                className="flex-1 outline-none bg-transparent text-base placeholder:text-neutral-400"
              />
              <button type="button" onClick={() => setSearchOpen(false)} className="text-sm text-neutral-500 hover:text-black">Close</button>
            </div>
          </form>
        )}
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-[85%] max-w-sm bg-white overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <span className="text-xl font-black">Menu</span>
              <button onClick={() => setMobileOpen(false)} className="p-2"><X className="w-5 h-5" /></button>
            </div>
            <div className="py-2" data-testid="mobile-nav-drawer">
              {NAV.map((cat) => (
                <MobileNavCategory key={cat.slug} cat={cat} onNavigate={() => setMobileOpen(false)} />
              ))}
              <Link to="/shop" onClick={() => setMobileOpen(false)} className="block px-5 py-3 text-base font-bold text-[#2C66E3]">Shop all products →</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

// ─── Mobile expandable nav category ───────────────────────────────────────
// Fixes the mobile mega-menu bug: the top-level link only ever took users
// to `/category/{slug}`, so `sections` / `items` were invisible on mobile.
// Now: chevron expands the sub-tree in place; the label itself remains a
// direct link to the category page.
function MobileNavCategory({ cat, onNavigate }) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = !!(cat.sections || (cat.items && cat.items.length > 0));

  return (
    <div className="border-b border-neutral-100">
      <div className="flex items-center">
        <Link
          to={`/category/${cat.slug}`}
          onClick={onNavigate}
          data-testid={`mobile-cat-${cat.slug}`}
          className="flex-1 block px-5 py-3 text-base font-medium hover:bg-neutral-50"
        >
          {cat.label}
        </Link>
        {hasChildren && (
          <button
            type="button"
            aria-label={expanded ? `Collapse ${cat.label}` : `Expand ${cat.label}`}
            aria-expanded={expanded}
            data-testid={`mobile-cat-toggle-${cat.slug}`}
            onClick={() => setExpanded((v) => !v)}
            className="px-5 py-3 text-neutral-600 hover:text-[#2C66E3]"
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
      {hasChildren && expanded && (
        <div className="bg-neutral-50 pl-3 pr-2 py-1">
          {cat.sections ? (
            cat.sections.map((section) => (
              <div key={section.title} className="py-2">
                <p className="text-[10px] tracking-widest text-[#2C66E3] uppercase font-extrabold px-3 mb-1">
                  {section.title}
                </p>
                <MobileNavItems items={section.items} catSlug={cat.slug} onNavigate={onNavigate} />
              </div>
            ))
          ) : (
            <MobileNavItems items={cat.items} catSlug={cat.slug} onNavigate={onNavigate} />
          )}
        </div>
      )}
    </div>
  );
}

function MobileNavItems({ items, catSlug, onNavigate }) {
  return (
    <div>
      {items.map((item) => {
        const isObj = typeof item === "object" && item !== null;
        const label = isObj ? item.label : item;
        const to = isObj
          ? (item.to ?? (item.slug ? `/product/${item.slug}` : `/category/${catSlug}`))
          : `/category/${catSlug}`;
        return (
          <Link
            key={label}
            to={to}
            onClick={onNavigate}
            data-testid={`mobile-menu-item-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="block px-3 py-2 text-sm text-neutral-700 hover:bg-white hover:text-[#2C66E3] rounded-lg"
          >
            {label}
          </Link>
        );
      })}
    </div>
  );
}
